import type { UpdateFlowMixinSettings } from '../../updateFlowMixin';
import { ReactiveGraphic } from './ReactiveGraphic';
import type { DeepReadonlyObject } from '../../../types';
import {
    AlphaAttribute,
    ColorAttribute,
    NumberAttribute,
    getAttributesToSerialize,
} from '../../attributes';
import { Config } from '../../../video-data/config';
import { getRoundedRectPerimeterPoints } from '../../../libs/roundedRectPath';
import { comparer, observable, reaction } from '../../reactivity';

type BoxBorderGraphicState = DeepReadonlyObject<
    Pick<
        BoxBorderGraphic,
        | 'boxWidth'
        | 'boxHeight'
        | 'start0'
        | 'end0'
        | 'start1'
        | 'end1'
        | 'horizontalPadding'
        | 'verticalPadding'
        | 'topLeftRadius'
        | 'topRightRadius'
        | 'bottomRightRadius'
        | 'bottomLeftRadius'
        | 'borderThickness'
        | 'borderColor'
        | 'borderAlpha'
    >
>;

export class BoxBorderGraphic extends ReactiveGraphic {
    override _classType = 'BoxBorderGraphic';
    override _name = Config.BOX_BORDER_GRAPHIC;

    @NumberAttribute()
    @observable
    accessor boxWidth = 150;
    @NumberAttribute()
    @observable
    accessor boxHeight = 150;
    @NumberAttribute()
    @observable
    accessor start0 = 0.25;
    @NumberAttribute()
    @observable
    accessor end0 = 0.75;
    @NumberAttribute()
    @observable
    accessor start1 = 0.75;
    @NumberAttribute()
    @observable
    accessor end1 = 0.25;
    @NumberAttribute({ animatable: false })
    @observable
    accessor horizontalPadding = 20;
    @NumberAttribute({ animatable: false })
    @observable
    accessor verticalPadding = 20;
    @NumberAttribute({ min: 0, animatable: false })
    @observable
    accessor topLeftRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    @observable
    accessor topRightRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    @observable
    accessor bottomRightRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    @observable
    accessor bottomLeftRadius = 0;
    @NumberAttribute({ min: 0, animatable: false })
    @observable
    accessor borderThickness = 5;
    @ColorAttribute()
    @observable
    accessor borderColor = 16777215;
    @AlphaAttribute()
    @observable
    accessor borderAlpha = 0.5;

    _points: number[][] = [];
    _segments: number[] = [];
    _length: number | null = null;
    #renderedGraphicsState: BoxBorderGraphicState | null = null;

    constructor(settings: UpdateFlowMixinSettings) {
        super(settings);
        this.addReaction(
            reaction(
                () => this.#graphicsState(),
                (state) => this.#applyGraphicsState(state),
                { name: 'BoxBorderGraphic.graphicsState' },
            ),
        );
    }

    #graphicsState(): BoxBorderGraphicState {
        return {
            boxWidth: this.boxWidth,
            boxHeight: this.boxHeight,
            start0: this.start0,
            end0: this.end0,
            start1: this.start1,
            end1: this.end1,
            horizontalPadding: this.horizontalPadding,
            verticalPadding: this.verticalPadding,
            topLeftRadius: this.topLeftRadius,
            topRightRadius: this.topRightRadius,
            bottomRightRadius: this.bottomRightRadius,
            bottomLeftRadius: this.bottomLeftRadius,
            borderThickness: this.borderThickness,
            borderColor: this.borderColor,
            borderAlpha: this.borderAlpha,
        };
    }

    #applyGraphicsState(state: BoxBorderGraphicState) {
        if (comparer.shallow(state, this.#renderedGraphicsState)) {
            return;
        }
        this.clear();
        this.updateCoords(state);
        this.lineStyle(state.borderThickness, state.borderColor, state.borderAlpha);
        this.lineForRange(state.start0, state.end0);
        this.lineForRange(state.start1, state.end1);
        this.#renderedGraphicsState = state;
    }

    positionOnSegment(uValue: number) {
        const position = uValue * this._length!;
        let absUValue = 0;
        let nAbsUValue = 0;
        for (let i = 0; i < this._segments.length; i++) {
            nAbsUValue += this._segments[i];
            if (position >= absUValue && position <= nAbsUValue) {
                const ni = i > this._points.length - 2 ? 0 : i + 1;
                const percentage = (position - absUValue) / this._segments[i];
                const x = this._points[i][0] * (1 - percentage) + this._points[ni][0] * percentage;
                const y = this._points[i][1] * (1 - percentage) + this._points[ni][1] * percentage;
                return {
                    index: i,
                    position: [x, y],
                };
            }
            absUValue += this._segments[i];
        }
    }

    lineForRange(start: number, end: number) {
        if (Math.abs(end - start) < 0.01) {
            return;
        }
        const spos = this.positionOnSegment(start % 1);
        const epos = this.positionOnSegment(end % 1);

        if (!spos || !epos) {
            return;
        }

        if (spos.index === epos.index) {
            this.moveTo(spos.position[0], spos.position[1]);
            this.lineTo(epos.position[0], epos.position[1]);
        } else if (spos.index < epos.index) {
            const nSegments = epos.index - spos.index;
            for (let i = 0; i < nSegments + 2; i++) {
                if (i === 0) {
                    this.moveTo(spos.position[0], spos.position[1]);
                } else if (i === nSegments + 1) {
                    this.lineTo(epos.position[0], epos.position[1]);
                } else {
                    this.lineTo(this._points[spos.index + i][0], this._points[spos.index + i][1]);
                }
            }
        } else {
            const nSegments = this._segments.length - spos.index + epos.index;
            for (let i = 0; i < nSegments + 2; i++) {
                if (i === 0) {
                    this.moveTo(spos.position[0], spos.position[1]);
                } else if (i === nSegments + 1) {
                    this.lineTo(epos.position[0], epos.position[1]);
                } else {
                    const index = (spos.index + i) % this._segments.length;
                    this.lineTo(this._points[index][0], this._points[index][1]);
                }
            }
        }
    }

    updateCoords(state: BoxBorderGraphicState) {
        const xMin = -state.horizontalPadding;
        const xMax = state.boxWidth + state.horizontalPadding;
        const yMin = -state.verticalPadding;
        const yMax = state.boxHeight + state.verticalPadding;

        this._points = getRoundedRectPerimeterPoints(xMin, yMin, xMax - xMin, yMax - yMin, {
            topLeftRadius: state.topLeftRadius,
            topRightRadius: state.topRightRadius,
            bottomRightRadius: state.bottomRightRadius,
            bottomLeftRadius: state.bottomLeftRadius,
        });

        this._segments = this._points.map((_point, index) => {
            const ci = index;
            let ni = index + 1;
            if (index === this._points.length - 1) {
                ni = 0;
            }
            const x = (this._points[ci][0] - this._points[ni][0]) ** 2;
            const y = (this._points[ci][1] - this._points[ni][1]) ** 2;
            return Math.sqrt(x + y);
        });

        this._length = this._segments.reduce((total, num) => {
            return total + num;
        });
    }

    protected override _calculateBounds() {
        this.#applyGraphicsState(this.#graphicsState());
        super._calculateBounds();
    }

    override get propertiesToSerialize() {
        return getAttributesToSerialize(this);
    }
}
